/**
 * MobileStickyHeader — Feb 2026 (v3)
 *
 * Sticky top bar shown on MOBILE ONLY (lg:hidden). Stays glued to viewport
 * top via `fixed top-0 left-0 right-0` — guaranteed to remain visible on any
 * scroll behaviour. Page layouts must reserve 56 px (`pt-14`) on their mobile
 * content wrapper so content does NOT slide underneath the header.
 *
 * Visual:
 *   - White background (#fff)
 *   - 3 px solid blue-700 (#1d4ed8) bottom border — only separator
 *   - No shadow, no rounded corners
 *   - Vaxa logo renders directly on the white bg (no wrapper / pill / padding box)
 *
 * Layout (left → right):
 *   [ Hamburger/X (44×44 dark icon) | "{userName} · {userRole}" centered | Logo 40×40 ]
 *
 * Props are pure UI — no routing/auth/data logic here.
 */
import React from 'react';
import { Menu, X } from 'lucide-react';

export default function MobileStickyHeader({
  userName = 'User',
  userRole = '',
  sidebarOpen = false,
  onMenuClick,
  logoSrc = '/logo-vaxa.png',
  logoAlt = 'Vaxa Medika',
  offsetTop = 0,
}) {
  const centerText = userRole ? `${userName} · ${userRole}` : userName;

  return (
    <header
      className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        borderBottom: '3px solid #1d4ed8',
        top: offsetTop,
      }}
      data-testid="mobile-sticky-header"
    >
      <div className="flex items-center justify-between h-14 px-2">
        {/* Hamburger/X — 44×44 touch target, dark icon on white bg */}
        <button
          type="button"
          onClick={onMenuClick}
          aria-label={sidebarOpen ? 'Tutup menu' : 'Buka menu'}
          className="flex items-center justify-center w-11 h-11 rounded-lg active:bg-gray-100 transition-colors flex-shrink-0"
          data-testid="mobile-header-menu-btn"
        >
          {sidebarOpen ? (
            <X className="w-6 h-6 text-gray-800" />
          ) : (
            <Menu className="w-6 h-6 text-gray-800" />
          )}
        </button>

        {/* Center: user · role, dark text on white */}
        <div className="flex-1 min-w-0 px-2 text-center">
          <span
            className="block text-sm font-semibold text-gray-900 truncate"
            data-testid="mobile-header-user-role"
          >
            {centerText}
          </span>
        </div>

        {/* Right: Vaxa logo, larger, no wrapper, direct on white bg */}
        <img
          src={logoSrc}
          alt={logoAlt}
          className="w-10 h-10 object-contain flex-shrink-0"
          data-testid="mobile-header-logo"
        />
      </div>
    </header>
  );
}
